import { useState, useEffect } from 'react';
import { Plus, Shield, Users, Trash2, Edit } from 'lucide-react';
import { Card, Table, Badge, Button, ConfirmModal, Input } from '@/components/common';
import { rbacService } from '@/services/rbacService';
import { formatDate } from '@/utils/format';
import type { Role } from '@/types';

export function RolesPage() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newRole, setNewRole] = useState({
    name: '',
    slug: '',
    description: '',
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await rbacService.getRoles({ per_page: 50 });
      setRoles(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load roles');
      console.error('Failed to fetch roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    setLoading(true);
    setError('');
    try {
      await rbacService.createRole(newRole);
      setCreateModalOpen(false);
      setNewRole({ name: '', slug: '', description: '' });
      fetchRoles();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create role');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;
    setLoading(true);
    try {
      await rbacService.deleteRole(roleToDelete);
      setDeleteModalOpen(false);
      setRoleToDelete(null);
      fetchRoles();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete role');
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    role.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns = [
    {
      key: 'name',
      title: 'Role',
      render: (_: any, role: Role) => (
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-blue-600" />
          <div>
            <p className="font-medium text-secondary-900">{role.name}</p>
            <p className="text-xs text-secondary-500">{role.slug}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'description',
      title: 'Description',
      render: (_: any, role: Role) => (
        <span className="text-sm text-secondary-600">
          {role.description || 'No description'}
        </span>
      ),
    },
    {
      key: 'permissions',
      title: 'Permissions',
      render: (_: any, role: Role) => (
        <Badge variant="info">
          {role.permissions?.length || 0} permissions
        </Badge>
      ),
    },
    {
      key: 'users',
      title: 'Users',
      render: (_: any, role: Role) => (
        <div className="flex items-center gap-1 text-sm text-secondary-600">
          <Users className="w-4 h-4" />
          {role.users_count || 0}
        </div>
      ),
    },
    {
      key: 'created',
      title: 'Created',
      render: (_: any, role: Role) => (
        <span className="text-sm text-secondary-600">
          {formatDate(role.created_at, 'MMM d, yyyy')}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: '100px',
      render: (_: any, role: Role) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              // Navigate to edit
            }}
            className="p-1.5 hover:bg-secondary-100 rounded transition-colors"
            title="Edit"
          >
            <Edit className="w-4 h-4 text-secondary-600" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setRoleToDelete(role.id);
              setDeleteModalOpen(true);
            }}
            className="p-1.5 hover:bg-red-100 rounded transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Roles & Permissions</h1>
          <p className="text-secondary-600 mt-1">Manage user roles and access control</p>
        </div>
        <Button
          icon={<Plus className="w-4 h-4" />}
          onClick={() => setCreateModalOpen(true)}
        >
          New Role
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="flex gap-4">
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search roles..."
          className="max-w-md"
        />
      </div>

      <Card padding="none">
        <Table
          data={filteredRoles}
          columns={columns}
          loading={loading}
          emptyMessage="No roles found"
        />
      </Card>

      {/* Create Role Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-2xl font-bold mb-4">Create Role</h2>
            <div className="space-y-4">
              <Input
                label="Role Name"
                value={newRole.name}
                onChange={(e) => {
                  const name = e.target.value;
                  const slug = name.toLowerCase().replace(/\s+/g, '-');
                  setNewRole({ ...newRole, name, slug });
                }}
                placeholder="Administrator"
                required
              />
              <Input
                label="Slug"
                value={newRole.slug}
                onChange={(e) => setNewRole({ ...newRole, slug: e.target.value })}
                placeholder="administrator"
                required
              />
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newRole.description}
                  onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                  placeholder="Full system access"
                  rows={2}
                  className="w-full px-4 py-2 rounded-lg border border-secondary-300 focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={handleCreateRole} disabled={loading || !newRole.name || !newRole.slug}>
                Create Role
              </Button>
              <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setRoleToDelete(null);
        }}
        onConfirm={handleDeleteRole}
        title="Delete Role"
        description="Are you sure you want to delete this role? Users with this role will lose their permissions."
        confirmText="Delete"
        variant="danger"
        loading={loading}
      />
    </div>
  );
}
